/**
 * Scene loader plugin for .zip format (Trace Scene with preview metadata).
 *
 * .zip trace scenes contain:
 * - info.json: TraceSceneInfoFile format with 'traces' array and preview data
 * - models.json: SceneInfo format with actual scene content
 * - assets/: Folder with GLB and other asset files
 *
 * Transform handling:
 * GLB root transforms are preserved. The scene transform was authored
 * expecting the GLB's internal root (e.g. Blender Z-up → Y-up rotation)
 * to remain, so Model loads with the default preserve path.
 */

import JSZip from 'jszip';
import { BaseTracePlugin, ParsedSceneStructure, ParsedSceneNode } from './BaseTracePlugin';
import type { ISceneLoaderPluginExtensions } from "@babylonjs/core/Loading/sceneLoader";
import type { SceneInfo } from '../../scenedata/sceneinfo';
import type { SceneObjectData } from '../../scenedata/sceneobjectdata';
import type { BehaviorData } from '../../scenedata/behaviordata';
import type { TraceSceneInfoFile } from '../../scenedata/infofiledata';
import { Utility } from '../../utils/Utility';

// =============================================================================
// Constants
// =============================================================================

const INFO_JSON_NAME = 'info.json';
const MODELS_FILENAME = 'models.json';

// =============================================================================
// Plugin Implementation
// =============================================================================

export class TraceZipSceneLoaderPlugin extends BaseTracePlugin {
    readonly name: string = "trace-zip";
    readonly extensions: ISceneLoaderPluginExtensions = {
        ".zip": { isBinary: true },
    };

    /**
     * Stores the original info.json for export round-tripping.
     * This is accessible after loading via getSourceInfoFile().
     */
    private _sourceInfoFile: TraceSceneInfoFile | null = null;

    /**
     * Get the original info.json data (for export round-tripping)
     */
    getSourceInfoFile(): TraceSceneInfoFile | null {
        return this._sourceInfoFile;
    }

    /**
     * Parse .zip archive into common scene structure
     */
    protected async parseSceneData(
        data: unknown,
        rootUrl: string,
        _fileName?: string
    ): Promise<ParsedSceneStructure> {
        // Load ZIP archive
        const zip = await JSZip.loadAsync(data as ArrayBuffer);

        // Extract assets to blobs for ResourceManager storage
        const assetBlobs = await this.extractAssets(zip);

        // Parse scene files (info.json and/or models.json)
        const sceneDataList = await this.parseSceneFiles(zip);

        // Convert SceneData array to ParsedSceneNode array
        const nodes = this.convertSceneData(sceneDataList);

        return {
            nodes,
            assetBlobs,
            rootUrl,
        };
    }

    /**
     * Extract assets from ZIP to blobs for ResourceManager storage.
     * Trace uses "assets/" folder (lowercase).
     */
    private async extractAssets(zip: JSZip): Promise<Map<string, Blob>> {
        const assetBlobs = new Map<string, Blob>();
        const assetsFolder = zip.folder('assets');

        if (assetsFolder) {
            const assetFiles = assetsFolder.filter((_relativePath, file) => !file.dir);
            for (const file of assetFiles) {
                const arrayBuffer = await file.async('arraybuffer');
                // Extract just the filename (remove 'assets/' prefix)
                const assetFilename = file.name.replace(/^assets\//i, '');
                const mimeType = Utility.mimeTypeFromFilename(assetFilename);
                const blob = new Blob([arrayBuffer], mimeType ? { type: mimeType } : undefined);

                // Store blob for ResourceManager (keyed by path like "assets/filename.jpg")
                assetBlobs.set(`assets/${assetFilename}`, blob);
            }
        }

        return assetBlobs;
    }

    /**
     * Parse info.json and/or models.json from the ZIP
     */
    private async parseSceneFiles(zip: JSZip): Promise<SceneObjectData[]> {
        const allSceneData: SceneObjectData[] = [];

        // Try to load info.json first
        const infoFile = zip.file(INFO_JSON_NAME);
        if (infoFile) {
            const text = await infoFile.async('string');
            const parsed = Utility.parseInfoJson(text);
            if (parsed) {
                // Store for export round-tripping
                this._sourceInfoFile = parsed.sourceInfoFile;

                // Apply migrations and collect scene data
                if (parsed.sceneInfo.content) {
                    this.applyMigrations(parsed.sceneInfo.content);
                    allSceneData.push(...parsed.sceneInfo.content);
                }
            }
        }

        // Try to load models.json
        const modelsFile = zip.file(MODELS_FILENAME);
        if (modelsFile) {
            const text = await modelsFile.async('string');
            const parsed = Utility.parseLegacyJson(text) as SceneInfo;

            if (parsed.content) {
                this.applyMigrations(parsed.content);
                allSceneData.push(...parsed.content);
            }
        }

        if (!infoFile && !modelsFile) {
            throw new Error('No info.json or models.json found in .zip trace scene');
        }

        return allSceneData;
    }

    /**
     * Apply migrations to scene data (components -> behaviors, resetAssetTransform)
     */
    private applyMigrations(sceneDataList: SceneObjectData[]): void {
        for (const sceneData of sceneDataList) {
            // MIGRATION: Support old "components" field name
            const legacy = sceneData as SceneObjectData & { components?: BehaviorData[] };
            if (legacy.components && !legacy.behaviors) {
                legacy.behaviors = legacy.components;
                delete legacy.components;
            }
        }
    }

    /**
     * Convert SceneData array to ParsedSceneNode array.
     * All asset loading is handled by behaviors (Model, Image, Video)
     * which load from ResourceManager using the src path.
     *
     * Transforms pass through verbatim. Anywhere `.zip` content is
     * already in glTF-RH (UnityGLTF mirrors X at zip-write time).
     * Image-marker `.zip` content, in Unity AR Foundation anchor-local
     * frame, is converted later by `loadCmsScene` via
     * {@link applyImageAnchorFrameConversion} — that layer is the one
     * that knows the scene has an image marker, so detection lives
     * there rather than in this loader.
     */
    private convertSceneData(
        sceneDataList: SceneObjectData[]
    ): ParsedSceneNode[] {
        return sceneDataList.map(sceneData => {
            return {
                name: sceneData.name,
                src: sceneData.src,
                transform: sceneData.transform ? {
                    translation: sceneData.transform.translation,
                    rotation: sceneData.transform.rotation,
                    scale: sceneData.transform.scale,
                } : undefined,
                parentName: sceneData.transform?.parent,
                active: typeof sceneData.active === 'boolean' ? sceneData.active : undefined,
                behaviors: sceneData.behaviors,
            };
        });
    }
}
